import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import {
  Refresh as RefreshIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalOffer as OfferIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  PictureAsPdf as PdfIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';

import { QRCodeCanvas } from 'qrcode.react';

import productStore from '../stores/productStore';
import glassTypeStore from '../stores/glassTypeStore';
import hardwareSetStore from '../stores/hardwareSetStore';
import partnershipStore from '../stores/partnershipStore';
import shopStore from '../stores/shopStore';
import productDimensionsStore from '../stores/productDimensionsStore';
import { getImageUrl } from '../utils/imageUtils';
import logger from '../utils/logger';
import ProductDimensionsEditor from '../components/ProductDimensionsEditor';
import ProductPhotoGallery from '../components/ProductPhotoGallery';
import ProductImageCarousel from '../components/ProductImageCarousel';
import { generateQuotePDF } from '../components/QuotePDFGenerator.jsx';
import { generateSimpleQuotePDF } from '../utils/simplePdfGenerator.jsx';
import apiClient from '../utils/apiClient';
import { MdContentCopy } from 'react-icons/md';
import { LuPrinter } from 'react-icons/lu';
import { FcCalculator } from 'react-icons/fc';

const DashboardPage = observer(() => {
  const [selectedGlassType, setSelectedGlassType] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedHardwareSet, setSelectedHardwareSet] = useState(null);
  const [isCompositionModalOpen, setIsCompositionModalOpen] = useState(false);
  const [isReplacementModalOpen, setIsReplacementModalOpen] = useState(false);
  const [selectedMappingForReplacement, setSelectedMappingForReplacement] = useState(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationPublicUrl, setCalculationPublicUrl] = useState(null);
  const [calculationError, setCalculationError] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Customer-facing quote is calculated and rounded by the backend.
  const getDealerCustomerPrice = (result) =>
    result?.calculation?.dealer_quote?.dealer_customer_price ?? 0;

  // Функция для проверки наличия торговых параметров
  const hasMarkupConfigured = () => {
    const markupPercentage = localStorage.getItem('markup_percentage');
    const markupAmount = localStorage.getItem('markup_amount');

    const hasPercentage =
      markupPercentage && !isNaN(parseFloat(markupPercentage)) && parseFloat(markupPercentage) > 0;
    const hasAmount =
      markupAmount && !isNaN(parseFloat(markupAmount)) && parseFloat(markupAmount) > 0;

    return hasPercentage || hasAmount;
  };

  // Функция для получения информации о текущей наценке
  const getCurrentMarkupInfo = () => {
    const markupPercentage = localStorage.getItem('markup_percentage');
    const markupAmount = localStorage.getItem('markup_amount');

    const hasPercentage =
      markupPercentage && !isNaN(parseFloat(markupPercentage)) && parseFloat(markupPercentage) > 0;
    const hasAmount =
      markupAmount && !isNaN(parseFloat(markupAmount)) && parseFloat(markupAmount) > 0;

    if (!hasPercentage && !hasAmount) {
      return null;
    }

    let info = [];
    if (hasPercentage) {
      info.push(`${markupPercentage}%`);
    }
    if (hasAmount) {
      info.push(`+${parseFloat(markupAmount).toLocaleString('ru-RU')} ₽`);
    }

    return info.join(' + ');
  };

  useEffect(() => {
    // Загружаем данные при монтировании компонента
    const loadData = async () => {
      try {
        // Сначала проверяем наличие магазина
        await shopStore.checkShop();

        if (shopStore.hasShop) {
          // Загружаем продукты партнера и типы стекла
          await Promise.all([
            productStore.fetchPartnerProducts(),
            glassTypeStore.fetchPartnerGlassTypes(),
          ]);
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
      }
    };

    loadData();
  }, []);

  const handleRefresh = async () => {
    try {
      await Promise.all([
        productStore.fetchPartnerProducts(),
        glassTypeStore.fetchPartnerGlassTypes(),
      ]);

      // Если был выбран продукт, перезагружаем его наборы фурнитуры и размеры
      if (selectedProduct) {
        await hardwareSetStore.fetchProductHardwareSets(selectedProduct.id);

        // Перезагружаем размеры продукта
        if (selectedProduct.default_dimensions) {
          productDimensionsStore.setDimensions(
            selectedProduct.id,
            selectedProduct.default_dimensions,
          );
        } else {
          productDimensionsStore.clearDimensions();
        }
      }
    } catch (error) {
      console.error('Ошибка при обновлении:', error);
    }
  };

  const handleGlassTypeSelect = (glassType) => {
    setSelectedGlassType(glassType);
    setCalculationResult(null); // Сбрасываем результат при смене стекла
    setCalculationPublicUrl(null); // Сбрасываем ссылку
    logger.log('Выбран тип стекла:', glassType.name, glassType.id);
  };

  const handleProductSelect = async (product) => {
    setSelectedProduct(product);
    setSelectedHardwareSet(null); // Сбрасываем выбранный набор при смене продукта
    setCalculationResult(null); // Сбрасываем результат при смене продукта
    setCalculationPublicUrl(null); // Сбрасываем ссылку

    logger.log('Выбрана модель:', product.name, product.id);

    try {
      // Загружаем наборы фурнитуры для выбранного продукта
      await hardwareSetStore.fetchProductHardwareSets(product.id);

      // Загружаем размеры продукта из default_dimensions
      if (product.default_dimensions) {
        productDimensionsStore.setDimensions(product.id, product.default_dimensions);
        logger.log('Загружены размеры продукта:', product.default_dimensions);
      } else {
        productDimensionsStore.clearDimensions();
        logger.log('У продукта нет размеров по умолчанию');
      }
    } catch (error) {
      console.error('Ошибка при загрузке данных продукта:', error);
    }
  };

  const handleHardwareSetSelect = async (hardwareSet) => {
    setSelectedHardwareSet(hardwareSet);
    setCalculationResult(null); // Сбрасываем результат при смене набора
    setCalculationPublicUrl(null); // Сбрасываем ссылку
    logger.log('Выбран набор фурнитуры:', hardwareSet.name, hardwareSet.id);

    try {
      // Загружаем состав набора для просмотра в модальном окне
      await hardwareSetStore.fetchHardwareItems(hardwareSet.id);
    } catch (error) {
      console.error('Ошибка при загрузке состава набора:', error);
    }
  };

  const handleOpenCompositionModal = () => {
    setIsCompositionModalOpen(true);
  };

  const handleCloseCompositionModal = () => {
    setIsCompositionModalOpen(false);
  };

  const handleOpenReplacementModal = (mappingItem) => {
    // Всегда получаем актуальные данные из store
    const actualMapping = hardwareSetStore.hardwareItems.find((item) => item.id === mappingItem.id);
    setSelectedMappingForReplacement(actualMapping || mappingItem);
    setIsReplacementModalOpen(true);
  };

  const handleCloseReplacementModal = () => {
    setIsReplacementModalOpen(false);
    setSelectedMappingForReplacement(null);
  };

  const handleReplaceHardware = (alternativeHardwareItemId) => {
    if (!selectedMappingForReplacement) return;

    try {
      // Выполняем локальную замену без сохранения в БД
      hardwareSetStore.replaceHardwareLocally(
        selectedMappingForReplacement.id,
        alternativeHardwareItemId,
      );

      // Сбрасываем результат расчета при замене фурнитуры
      setCalculationResult(null);
      setCalculationPublicUrl(null); // Сбрасываем ссылку

      // Закрываем модальное окно после применения изменений
      handleCloseReplacementModal();
      logger.log('Локальная замена выполнена успешно');
    } catch (error) {
      console.error('Ошибка при замене:', error);
    }
  };

  const handlePhotoClick = (photoUrl, altText, event) => {
    event.stopPropagation(); // Предотвращаем всплытие к карточке
    setSelectedPhoto({ url: photoUrl, alt: altText });
    setIsPhotoModalOpen(true);
  };

  const handleClosePhotoModal = () => {
    setIsPhotoModalOpen(false);
    setSelectedPhoto(null);
  };

  const handleOpenGalleryModal = () => {
    setIsGalleryModalOpen(true);
  };

  const handleCloseGalleryModal = () => {
    setIsGalleryModalOpen(false);
  };

  // Генерация и скачивание PDF
  const handleDownloadPDF = async () => {
    try {
      logger.log('🎯 Начинаем генерацию PDF с кириллицей...');

      // Собираем все данные для PDF
      const pdfData = {
        shopData: shopStore.shop,
        productData: {
          ...selectedProduct,
          img_url: selectedProduct?.img_url ? getImageUrl(selectedProduct.img_url) : null,
        },
        dimensionsData: productDimensionsStore.hasDimensions
          ? productDimensionsStore.getFinalDimensions()
          : null,
        glassData: selectedGlassType,
        hardwareData: {
          ...selectedHardwareSet,
          hardware_items: hardwareSetStore.hasHardwareItems
            ? hardwareSetStore.hardwareItems.map((item) => ({
                quantity: item.quantity,
                hardware_details: {
                  name: item.hardware.name,
                  display_name: item.hardware.display_name,
                },
              }))
            : [],
        },
        calculationResult,
        finalPrice: getDealerCustomerPrice(calculationResult),
      };

      logger.log('📋 Данные для PDF:', pdfData);

      // Создаем имя файла с кириллицей
      const productName = selectedProduct?.name || 'душевая_кабина';
      const date = new Date().toLocaleDateString('ru-RU').replace(/\./g, '-');
      const filename = `КП_${productName}_${date}.pdf`;

      try {
        logger.log('🔨 Создаем PDF документ через HTML/Canvas...');
        // Создаем PDF документ с полной поддержкой кириллицы
        const pdfGenerator = await generateQuotePDF(pdfData);

        logger.log('📥 Скачиваем файл...');
        // Скачиваем файл
        pdfGenerator.download(filename);

        logger.log(
          '✅ PDF с кириллицей успешно сгенерирован и скачан через HTML/Canvas:',
          filename,
        );
      } catch (htmlCanvasError) {
        logger.log(
          '⚠️ html2canvas не работает, используем простой PDF генератор:',
          htmlCanvasError.message,
        );

        // Fallback к простому PDF генератору
        const simplePdfGenerator = generateSimpleQuotePDF(pdfData);
        simplePdfGenerator.download(filename);

        logger.log('✅ PDF с кириллицей успешно сгенерирован через простой генератор:', filename);
      }
    } catch (error) {
      console.error('❌ Ошибка при генерации PDF:', error);
      console.error('❌ Детали ошибки:', error.stack);
      alert('Произошла ошибка при генерации PDF. Проверьте консоль для подробностей.');
    }
  };

  // Сохранение расчета и получение ссылки
  const saveCalculationAndGetLink = async (calculationData) => {
    try {
      logger.log('💾 Сохранение расчета...');

      // Вычисляем розничную цену магазина (с наценкой)
      const response = await apiClient.post('/save-calculation', {
        calculationData,
      });

      if (response.data.success) {
        logger.log('✅ Расчет сохранен, получена ссылка:', response.data.public_url);
        setCalculationPublicUrl(response.data.public_url);
      }
    } catch (error) {
      console.error('❌ Ошибка при сохранении расчета:', error);
      // Не показываем ошибку пользователю, просто не отображаем ссылку
    }
  };

  // Отправка в WhatsApp (пока без PDF)
  const handleSendToWhatsApp = () => {
    const phoneNumber = localStorage.getItem('whatsapp_number') || '';

    let message =
      `Коммерческое предложение\n\n` +
      `Модель: ${selectedProduct?.name || 'Не указано'}\n` +
      `Тип стекла: ${selectedGlassType?.name || 'Не указано'}\n` +
      `Набор фурнитуры: ${selectedHardwareSet?.name || 'Не указано'}\n` +
      `Стоимость: ${getDealerCustomerPrice(calculationResult).toLocaleString(
        'ru-RU',
      )} ₽\n\n`;

    // Добавляем ссылку на расчет если она есть
    if (calculationPublicUrl) {
      message += `Ссылка на расчет: ${calculationPublicUrl}\n\n`;
    }

    message +=
      `${shopStore.shop?.name || 'Наш магазин'}\n` +
      `${shopStore.shop?.contact_info?.['тел'] || ''}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = phoneNumber
      ? `https://wa.me/${phoneNumber.replace(/[^\d]/g, '')}?text=${encodedMessage}`
      : `https://wa.me/?text=${encodedMessage}`;

    window.open(whatsappUrl, '_blank');
    logger.log('📱 Переход в WhatsApp');
  };

  // Если нет магазина
  if (!shopStore.hasShop && !shopStore.isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Для начала работы необходимо зарегистрировать магазин
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '1.6rem', sm: '2.125rem' }, // Меньший размер на мобилках
            }}>
            <ShoppingCartIcon color="primary" />
            Каталог продукции
          </Typography>

          {/* Индикатор текущей наценки */}
          {/*         {getCurrentMarkupInfo() && (
            <Chip
              label={`Наценка: ${getCurrentMarkupInfo()}`}
              color="success"
              variant="outlined"
              size="small"
              icon={<OfferIcon />}
              sx={{ fontSize: '0.75rem' }}
            />
          )} */}
        </Box>

        {/* Кнопка для отладки локальных замен */}
        {hardwareSetStore.localReplacements.length > 0 && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              console.log('Локальные замены:', hardwareSetStore.localReplacements);
              console.log('Текущий состав:', hardwareSetStore.hardwareItems);
            }}>
            Замены ({hardwareSetStore.localReplacements.length})
          </Button>
        )}
      </Box>

      {/* Ошибки */}
      {(productStore.error || glassTypeStore.error || hardwareSetStore.error) && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => {
            productStore.clearError();
            glassTypeStore.clearError();
            hardwareSetStore.clearError();
          }}>
          {productStore.error || glassTypeStore.error || hardwareSetStore.error}
        </Alert>
      )}

      {/* Загрузка */}
      {(productStore.isLoading || glassTypeStore.isLoading || hardwareSetStore.isLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Продукты */}
      {!productStore.isLoading && productStore.hasProducts && (
        <Paper elevation={4} sx={{ p: 2, maxWidth: 500, mb: 4 }}>
          <FormControl fullWidth size="medium">
            <InputLabel id="shop-product-select-label">Выберите модель</InputLabel>
            <Select
              labelId="shop-product-select-label"
              id="shop-product-select"
              label="Выберите модель"
              value={selectedProduct?.id ?? ''}
              onChange={(event) => {
                const newId = event.target.value;
                const found =
                  productStore.activeProducts.find((p) => String(p.id) === String(newId)) || null;
                if (found) {
                  handleProductSelect(found);
                }
              }}
              renderValue={(selectedId) => {
                const prod = productStore.activeProducts.find(
                  (p) => String(p.id) === String(selectedId),
                );
                if (!prod) return '';
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={prod.img_url ? getImageUrl(prod.img_url) : undefined}
                      alt={prod.name}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    />
                    <Typography>{prod.name}</Typography>
                  </Box>
                );
              }}>
              {productStore.activeProducts.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={option.img_url ? getImageUrl(option.img_url) : undefined}
                      alt={option.name}
                      variant="rounded"
                      sx={{ width: 56, height: 56 }}
                    />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {option.name}
                      </Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {/* Фото выбранного продукта */}
      {selectedProduct && (
        <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Фото модели: {selectedProduct.name}
          </Typography>
          <ProductImageCarousel
            product={selectedProduct}
            glassTypeId={selectedGlassType?.id}
            hardwareSetId={selectedHardwareSet?.id}
            glassTypeName={selectedGlassType?.name}
            hardwareSetName={selectedHardwareSet?.name}
            photosEndpoint={`/partnerships/partner-product-photos/${selectedProduct.id}`}
            onImageClick={handlePhotoClick}
            imageSx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: 4,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Нажмите для увеличения
          </Typography>
        </Box>
      )}

      {/* Кнопка "Посмотреть образцы" */}
      {selectedProduct && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PhotoLibraryIcon />}
            onClick={handleOpenGalleryModal}
            sx={{
              px: 3,
              py: 1.5,
              fontSize: '1rem',
              textTransform: 'none',
            }}>
            Посмотреть образцы модели
          </Button>
        </Box>
      )}

      {/* Редактор размеров для выбранного продукта */}
      {selectedProduct && (
        <ProductDimensionsEditor
          selectedProduct={selectedProduct}
          onDimensionsChange={() => setCalculationResult(null)}
        />
      )}

      {/* Наборы фурнитуры для выбранного продукта */}
      {selectedProduct && hardwareSetStore.hasHardwareSets && (
        <Box sx={{ mb: 4, mt: 4 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Наборы фурнитуры для &quot;{selectedProduct.name}&quot;
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {hardwareSetStore.sortedHardwareSets.map((hardwareSet) => (
              <Button
                key={hardwareSet.id}
                variant={selectedHardwareSet?.id === hardwareSet.id ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleHardwareSetSelect(hardwareSet)}
                sx={{
                  minWidth: 'auto',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  px: 2,
                  py: 0.5,
                }}>
                {hardwareSet.name}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* Каталог стекла */}
      {glassTypeStore.hasGlassTypes && (
        <Box sx={{ mb: 4, mt: 4 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Тип стекла
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {glassTypeStore.glassTypes.map((glassType) => (
              <Button
                key={glassType.id}
                variant={selectedGlassType?.id === glassType.id ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleGlassTypeSelect(glassType)}
                sx={{
                  minWidth: 'auto',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  px: 2,
                  py: 0.5,
                }}>
                {glassType.name}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* Пустое состояние */}
      {!productStore.isLoading && !productStore.hasProducts && !productStore.error && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Продукты не найдены
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {productStore.partnership
              ? 'У вашего партнера пока нет активных продуктов'
              : 'Нет активных партнерств с производителями'}
          </Typography>
          <Button variant="outlined" onClick={handleRefresh}>
            Проверить снова
          </Button>
        </Box>
      )}

      {/* Итоговая информация о выборе */}
      {(selectedProduct || selectedHardwareSet || selectedGlassType) && (
        <Card
          sx={{
            mt: 4,
            p: { xs: 3, sm: 4 },
            bgcolor: 'primary.50',
            border: 2,
            borderColor: 'primary.light',
            borderRadius: 2,
          }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Текущий выбор:
            </Typography>

            {selectedProduct && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Модель:</strong> {selectedProduct.name}
              </Typography>
            )}

            {productDimensionsStore.hasDimensions && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Размеры кабины:</strong>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 2 }}>
                  {productDimensionsStore.sortedDimensions.map(([key, dimension]) => (
                    <Chip
                      key={key}
                      label={`${dimension.label}: ${dimension.value} мм`}
                      size="small"
                      color={dimension.has_glass ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ fontSize: '0.8rem' }}
                    />
                  ))}
                </Box>
                {productDimensionsStore.isModified && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ fontStyle: 'italic', ml: 2 }}>
                    Размеры изменены относительно значений по умолчанию
                  </Typography>
                )}
              </Box>
            )}

            {selectedHardwareSet && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="body1">
                  <strong>Набор фурнитуры:</strong> {selectedHardwareSet.name}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={handleOpenCompositionModal}
                  disabled={hardwareSetStore.isLoadingItems || !hardwareSetStore.hasHardwareItems}
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? 'auto' : 150,
                    px: isMobile ? 1.5 : 2,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                  }}>
                  {isMobile ? 'Состав' : 'Посмотреть состав'}
                </Button>
              </Box>
            )}
            {selectedGlassType && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Тип стекла:</strong> {selectedGlassType.name}
              </Typography>
            )}

            {selectedProduct && selectedHardwareSet && selectedGlassType && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  disabled={isCalculating}
                  startIcon={isCalculating ? <CircularProgress size={20} color="inherit" /> : null}
                  onClick={async () => {
                    setIsCalculating(true);
                    setCalculationError(null);
                    setCalculationResult(null);

                    // Собираем полную информацию для расчета
                    const calculationData = {
                      // Информация о продукте
                      product: {
                        id: selectedProduct.id,
                        name: selectedProduct.name,
                        description: selectedProduct.description,
                        geometry_type: selectedProduct.geometry_type,
                        manufacturer_id: selectedProduct.manufacturer_id,
                      },

                      // Информация о размерах - все в одном объекте
                      dimensions: productDimensionsStore.hasDimensions
                        ? productDimensionsStore.getFinalDimensions()
                        : null,

                      // Информация о наборе фурнитуры
                      hardware_set: {
                        id: selectedHardwareSet.id,
                        name: selectedHardwareSet.name,
                        description: selectedHardwareSet.description,
                        manufacturer_id: selectedHardwareSet.manufacturer_id,
                        product_id: selectedHardwareSet.product_id,
                        sort_order: selectedHardwareSet.sort_order,
                        // Состав набора
                        hardware_items: hardwareSetStore.hasHardwareItems
                          ? hardwareSetStore.hardwareItems.map((item) => ({
                              mapping_id: item.id,
                              hardware_item_id: item.hardware_item_id,
                              original_hardware_item_id: item.original_hardware_item_id,
                              quantity: item.quantity,
                              allow_replacement: item.allow_replacement,
                              is_replaced: item.hardware_item_id !== item.original_hardware_item_id,
                              hardware_details: {
                                id: item.hardware.id,
                                name: item.hardware.name,
                                display_name: item.hardware.display_name,
                                article: item.hardware.article,
                                manufacturer_id: item.hardware.manufacturer_id,
                                img_url: item.hardware.img_url,
                              },
                            }))
                          : [],
                        // Локальные замены
                        local_replacements: hardwareSetStore.localReplacements,
                      },

                      // Информация о типе стекла
                      glass_type: {
                        id: selectedGlassType.id,
                        name: selectedGlassType.name,
                        description: selectedGlassType.description,
                        manufacturer_id: selectedGlassType.manufacturer_id,
                        thickness: selectedGlassType.thickness,
                        img_url: selectedGlassType.img_url,
                        // Цены с партнерскими скидками (если они есть)
                        partner_price_per_sqm: selectedGlassType.partner_price_per_sqm,
                        original_price_per_sqm: selectedGlassType.price_per_sqm,
                      },

                      // Метаинформация
                      meta: {
                        timestamp: new Date().toISOString(),
                        shop_id: shopStore.shop?.id,
                        partnership_id: productStore.partnership?.id,
                        partner_manufacturer_id: productStore.partnership?.manufacturer_id,
                      },

                      // Торговая наценка магазина
                      shop_markup: {
                        markup_percentage: localStorage.getItem('markup_percentage') || '0',
                        markup_amount: localStorage.getItem('markup_amount') || '0',
                      },
                    };

                    console.log('=== ДАННЫЕ ДЛЯ РАСЧЕТА СТОИМОСТИ ===');
                    console.log('📋 Полная конфигурация:', calculationData);

                    // Отправляем данные на бекенд для расчета
                    try {
                      const response = await apiClient.post('/calculate-cost', calculationData);
                      console.log('✅ Ответ от бекенда:', response.data);

                      if (response.data.success) {
                        setCalculationResult(response.data);

                        // Автоматически сохраняем расчет и получаем ссылку
                        await saveCalculationAndGetLink(calculationData);
                      }
                    } catch (error) {
                      console.error(
                        '❌ Ошибка отправки на бекенд:',
                        error.response?.data || error.message,
                      );
                      setCalculationResult(null);
                      setCalculationPublicUrl(null);
                      setCalculationError(
                        error.response?.data?.message
                          || error.response?.data?.error
                          || 'Не удалось рассчитать цену. Проверьте конфигурацию изделия.',
                      );
                    } finally {
                      setIsCalculating(false);
                    }
                  }}>
                  <FcCalculator
                    style={{
                      marginRight: 6,
                      fontSize: 20 
                    }}
                  />

                  {isCalculating ? 'Расчет...' : 'Рассчитать'}
                </Button>
              </Box>
            )}
          </Alert>
        </Card>
      )}

      {calculationError && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => setCalculationError(null)}>
          {calculationError}
        </Alert>
      )}

      {/* Результат расчета */}
      {calculationResult && (
        <Card
          sx={{
            mt: 3,
            p: 3,
            bgcolor: hasMarkupConfigured() ? 'success.50' : 'error.50',
            border: 2,
            borderColor: hasMarkupConfigured() ? 'success.main' : 'error.main',
          }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h4"
              component="div"
              sx={{
                color: hasMarkupConfigured() ? 'success.main' : 'error.main',
                fontWeight: 'bold',
                mb: 1,
              }}>
              {getDealerCustomerPrice(calculationResult).toLocaleString('ru-RU')} ₽
            </Typography>

            {/* Предупреждение если торговые параметры не настроены */}
            {!hasMarkupConfigured() && (
              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  bgcolor: 'warning.50',
                  border: 1,
                  borderColor: 'warning.main',
                  textAlign: 'center',
                  '& .MuiAlert-icon': {
                    color: 'warning.main',
                  },
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
                icon={<WarningIcon />}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 1,
                    width: '100%',
                  }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Убедитесь, что торговые параметры магазина выставлены правильно
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SettingsIcon />}
                    href="/options"
                    sx={{ mt: 1 }}>
                    Перейти в Настройки
                  </Button>
                </Box>
              </Alert>
            )}

            {calculationResult.calculation.glass_area && (
              <Typography variant="body2" color="text.secondary">
                Площадь стекла: {Number(calculationResult.calculation.glass_area).toFixed(1)} м²
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Расчет выполнен {new Date(calculationResult.timestamp).toLocaleString('ru-RU')}
            </Typography>

            {/* Ссылка на расчет для клиента */}
            {calculationPublicUrl && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'info.50',
                  border: 1,
                  borderColor: 'info.main',
                  borderRadius: 1,
                }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: 'bold', color: 'info.main' }}>
                  🔗 Ссылка для клиента:
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    gap: 1,
                    justifyContent: 'center',
                  }}>
                  <Typography
                    variant="body2"
                    component="a"
                    href={calculationPublicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontWeight: 'medium',
                      wordBreak: 'break-all',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}>
                    {calculationPublicUrl}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      navigator.clipboard.writeText(calculationPublicUrl);
                      logger.log('📋 Ссылка скопирована в буфер обмена');
                    }}
                    sx={{
                      minWidth: 'auto',
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem',
                    }}>
                    <MdContentCopy
                      style={{
                        marginRight: 6,
                      }}
                    />{' '}
                    Копировать
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      try {
                        const printUrl = `${calculationPublicUrl}${
                          calculationPublicUrl.includes('?') ? '&' : '?'
                        }print=1`;
                        const newWindow = window.open(printUrl, '_blank');
                        if (!newWindow) {
                          logger.log('❗ Не удалось открыть окно печати (pop-up заблокирован)');
                          // Фолбэк: открываем просто ссылку
                          window.open(calculationPublicUrl, '_blank');
                        }
                      } catch (e) {
                        logger.error('Ошибка при попытке печати:', e);
                        window.open(calculationPublicUrl, '_blank');
                      }
                    }}
                    sx={{
                      minWidth: 'auto',
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem',
                      display: { xs: 'none', sm: 'inline-flex' }, // скрыть на мобильных
                    }}>
                    <LuPrinter
                      style={{
                        marginRight: 6,
                      }}
                    />
                    Печать расчета
                  </Button>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  Отправьте эту ссылку клиенту для просмотра расчета
                </Typography>

                {/* QR-код для сканирования */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mt: 2,
                    pt: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                    Или отсканируйте QR-код:
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'white',
                      borderRadius: 1,
                      boxShadow: 1,
                    }}>
                    <QRCodeCanvas
                      value={calculationPublicUrl}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Кнопки для PDF и WhatsApp */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mt: 3,
                justifyContent: 'center',
              }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PdfIcon />}
                onClick={handleDownloadPDF}
                sx={{
                  px: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  textTransform: 'none',
                  minWidth: { xs: '100%', sm: 'auto' },
                }}>
                Скачать PDF
              </Button>

              <Button
                variant="outlined"
                color="success"
                startIcon={<WhatsAppIcon />}
                onClick={handleSendToWhatsApp}
                sx={{
                  px: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  textTransform: 'none',
                  minWidth: { xs: '100%', sm: 'auto' },
                  borderColor: '#25d366',
                  color: '#25d366',
                  '&:hover': {
                    borderColor: '#128c7e',
                    backgroundColor: 'rgba(37, 211, 102, 0.04)',
                  },
                }}>
                Отправить в WhatsApp
              </Button>
            </Box>
          </Box>
        </Card>
      )}

      {/* Модальное окно состава набора фурнитуры */}
      <Dialog
        open={isCompositionModalOpen}
        onClose={handleCloseCompositionModal}
        maxWidth="md"
        fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Состав набора &quot;{selectedHardwareSet?.name}&quot;
            </Typography>
            <IconButton onClick={handleCloseCompositionModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {hardwareSetStore.isLoadingItems && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!hardwareSetStore.isLoadingItems && hardwareSetStore.hasHardwareItems && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 80 }}>Фото</TableCell>
                    <TableCell>Наименование</TableCell>
                    <TableCell align="center" sx={{ width: 120 }}>
                      Количество
                    </TableCell>
                    <TableCell align="center" sx={{ width: 120 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hardwareSetStore.sortedHardwareItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Avatar
                          src={
                            item.hardware.img_url ? getImageUrl(item.hardware.img_url) : undefined
                          }
                          alt={item.hardware.display_name || item.hardware.name}
                          sx={{
                            width: 60,
                            height: 60,
                            bgcolor: 'grey.200',
                            cursor: item.hardware.img_url ? 'zoom-in' : 'default',
                            '&:hover': item.hardware.img_url
                              ? {
                                  opacity: 0.8,
                                  transform: 'scale(1.05)',
                                }
                              : {},
                          }}
                          variant="rounded"
                          onClick={
                            item.hardware.img_url
                              ? (event) =>
                                  handlePhotoClick(
                                    getImageUrl(item.hardware.img_url),
                                    item.hardware.display_name || item.hardware.name,
                                    event,
                                  )
                              : undefined
                          }>
                          {!item.hardware.img_url && (
                            <Typography
                              variant="caption"
                              sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                              Нет фото
                            </Typography>
                          )}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {item.hardware.display_name || item.hardware.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${item.quantity} шт.`}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {(item.allow_replacement &&
                          item.possible_replacements &&
                          item.possible_replacements.length > 0) ||
                        (item.original_hardware_item_id &&
                          item.hardware_item_id !== item.original_hardware_item_id) ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color={
                              item.hardware_item_id !== item.original_hardware_item_id
                                ? 'secondary'
                                : 'primary'
                            }
                            onClick={() => handleOpenReplacementModal(item)}
                            disabled={hardwareSetStore.isLoadingItems}>
                            {item.hardware_item_id !== item.original_hardware_item_id
                              ? 'Изменено'
                              : 'Заменить'}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!hardwareSetStore.isLoadingItems && !hardwareSetStore.hasHardwareItems && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Состав набора не найден
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseCompositionModal} variant="outlined">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модальное окно выбора замены */}
      <Dialog
        open={isReplacementModalOpen}
        onClose={handleCloseReplacementModal}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Выберите замену</Typography>
            <IconButton onClick={handleCloseReplacementModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedMappingForReplacement && (
            <>
              <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.secondary' }}>
                Текущий выбор:{' '}
                <strong>
                  {selectedMappingForReplacement.hardware.display_name ||
                    selectedMappingForReplacement.hardware.name}
                </strong>
                {selectedMappingForReplacement.original_hardware_item_id &&
                  selectedMappingForReplacement.hardware_item_id !==
                    selectedMappingForReplacement.original_hardware_item_id && (
                    <Chip label="Изменено" size="small" color="secondary" sx={{ ml: 1 }} />
                  )}
              </Typography>

              <Typography variant="body2" sx={{ mb: 3 }}>
                Кликните на карточку для замены, или на фото для увеличения:
              </Typography>

              {(() => {
                const extendedReplacements = hardwareSetStore.getExtendedReplacementsForItem(
                  selectedMappingForReplacement,
                );
                return extendedReplacements && extendedReplacements.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {extendedReplacements.map((replacement) => (
                      <Card
                        key={replacement.id}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: replacement.is_original ? 2 : 0,
                          borderColor: replacement.is_original ? 'success.main' : 'transparent',
                          bgcolor: replacement.is_original ? 'success.50' : 'background.paper',
                          '&:hover': {
                            bgcolor: replacement.is_original ? 'success.100' : 'action.hover',
                            transform: 'translateY(-1px)',
                            boxShadow: 2,
                          },
                        }}
                        onClick={() =>
                          handleReplaceHardware(replacement.alternative_hardware_item_id)
                        }>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={
                                replacement.alternative_hardware.img_url
                                  ? getImageUrl(replacement.alternative_hardware.img_url)
                                  : undefined
                              }
                              alt={
                                replacement.alternative_hardware.display_name ||
                                replacement.alternative_hardware.name
                              }
                              sx={{
                                width: 50,
                                height: 50,
                                bgcolor: 'grey.200',
                                cursor: replacement.alternative_hardware.img_url
                                  ? 'zoom-in'
                                  : 'default',
                                '&:hover': replacement.alternative_hardware.img_url
                                  ? {
                                      opacity: 0.8,
                                      transform: 'scale(1.05)',
                                    }
                                  : {},
                              }}
                              variant="rounded"
                              onClick={
                                replacement.alternative_hardware.img_url
                                  ? (event) =>
                                      handlePhotoClick(
                                        getImageUrl(replacement.alternative_hardware.img_url),
                                        replacement.alternative_hardware.display_name ||
                                          replacement.alternative_hardware.name,
                                        event,
                                      )
                                  : undefined
                              }>
                              {!replacement.alternative_hardware.img_url && (
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: '0.6rem', textAlign: 'center' }}>
                                  Нет фото
                                </Typography>
                              )}
                            </Avatar>

                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                  {replacement.alternative_hardware.display_name ||
                                    replacement.alternative_hardware.name}
                                </Typography>
                                {replacement.is_original && (
                                  <Chip
                                    label="Оригинал"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              {replacement.alternative_hardware.article && (
                                <Typography variant="caption" color="text.secondary">
                                  Артикул: {replacement.alternative_hardware.article}
                                </Typography>
                              )}
                              {replacement.is_original && (
                                <Typography
                                  variant="caption"
                                  color="success.main"
                                  sx={{ fontWeight: 'bold' }}>
                                  Вернуться к исходной фурнитуре
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center', py: 4 }}>
                    Нет доступных вариантов замены
                  </Typography>
                );
              })()}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseReplacementModal} variant="outlined">
            Отмена
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модальное окно для просмотра увеличенного фото */}
      <Dialog open={isPhotoModalOpen} onClose={handleClosePhotoModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Просмотр фото</Typography>
            <IconButton onClick={handleClosePhotoModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', p: 2 }}>
          {selectedPhoto && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img
                src={selectedPhoto.url}
                alt={selectedPhoto.alt}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  mt: 2,
                  color: 'text.secondary',
                  textAlign: 'center',
                }}>
                {selectedPhoto.alt}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePhotoModal} variant="outlined">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Галерея фотографий продукта */}
      <ProductPhotoGallery
        open={isGalleryModalOpen}
        onClose={handleCloseGalleryModal}
        productId={selectedProduct?.id}
        productName={selectedProduct?.name}
      />
    </Container>
  );
});

export default DashboardPage;
